import { useCallback, useEffect, useState } from "react";

import { formatCurrency, transformDateToLocalDateString } from "helpers";
import { getUsageEventAPI } from "services/api/finance/usage/api";
import { USAGE_KEY_LABELS } from "services/api/finance/usage/consts";
import { UsageEvent } from "services/api/finance/usage/types";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";

import { UsageDetailModalProps } from "./types";

/** Vrijednosti iz JSON-a mogu biti bilo čega; prikazuju se onako kako su stigle. */
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Da" : "Ne";
  if (Array.isArray(value)) return value.length === 0 ? "—" : value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
};

const DetailSection: React.FC<{ title: string; data: Record<string, any> }> = ({
  title,
  data,
}) => {
  const entries = Object.entries(data).filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );

  if (entries.length === 0) return null;

  return (
    <div className="border border-slate-200 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-3">
        {title}
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {entries.map(([key, value]) => (
          <div key={key} className="flex justify-between gap-4">
            <span className="text-slate-500">{key}</span>
            <span className="font-medium text-slate-900 text-right break-all">
              {formatValue(value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Detalj jednog događaja.
 * <para>
 * Za izračune pokazuje obje strane: što je uneseno i što je vraćeno. Za SMS i učitane račune
 * detalj stoji u vlastitom zapisu, pa se ovdje vidi samo ono što se naplacuje.
 * </para>
 */
const UsageDetailModal: React.FC<UsageDetailModalProps> = ({
  show,
  usageEventId,
  onClose,
}) => {
  const [usageEvent, setUsageEvent] = useState<UsageEvent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchUsageEvent = useCallback(async (id: string) => {
    setIsLoading(true);

    try {
      setUsageEvent(await getUsageEventAPI(id));
    } catch {
      // poruka ide kroz snackbar; radije prazno nego pogrešan zapis
      setUsageEvent(null);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!show || !usageEventId) {
      setUsageEvent(null);
      return;
    }

    fetchUsageEvent(usageEventId);
  }, [show, usageEventId, fetchUsageEvent]);

  const vrsta = usageEvent
    ? USAGE_KEY_LABELS[usageEvent.pricingKeyCode] ?? usageEvent.pricingKeyCode
    : "";

  return (
    <Modal show={show} position="centered" size="medium" onHide={onClose}>
      <ModalHeader title="Detalji izračuna" onHide={onClose} />

      <ModalBody>
        <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          {isLoading && <Loader isLoading={true} message="Učitavanje…">{null}</Loader>}

          {usageEvent && (
            <>
              <div className="border border-slate-200 px-4 py-3">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Vrsta</span>
                    <span className="font-medium text-slate-900">{vrsta}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Datum</span>
                    <span className="font-medium text-slate-900">
                      {transformDateToLocalDateString(new Date(usageEvent.occurredOn))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Predmet</span>
                    <span className="font-medium text-slate-900">{usageEvent.subject}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Korisnik</span>
                    <span className="font-medium text-slate-900">
                      {usageEvent.userCreatedFullName ?? "—"}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-2 mt-1">
                    <span className="text-slate-500">Naplaćeno</span>
                    <span className="font-semibold text-slate-900">
                      {usageEvent.isSuccess
                        ? formatCurrency.format(usageEvent.amount)
                        : "Nije naplaćeno"}
                    </span>
                  </div>
                </div>
              </div>

              {!usageEvent.isSuccess && usageEvent.errorMessage && (
                <div className="border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm text-red-800 font-medium">Izračun nije prošao</p>
                  <p className="text-sm text-red-600 mt-1">{usageEvent.errorMessage}</p>
                </div>
              )}

              {usageEvent.request && (
                <DetailSection title="Uneseno" data={usageEvent.request} />
              )}

              {usageEvent.response && (
                <DetailSection title="Dobiveno" data={usageEvent.response} />
              )}
            </>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-end px-4 py-3">
          <Button variant="outlined" onClick={onClose}>
            Zatvori
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default UsageDetailModal;
